/**
 * SystemHealth.tsx — FR-014 (Twin #2) / T314.
 *
 * Binds `api.backgroundTasks.listRecent` — the REAL twin
 * (convex/backgroundTasks.ts:25, the lone real-bodied stub-signature
 * query per specs/exploration/stub_orphan_inventory.md §3) — and NOT
 * the empty-array stub `api.deliveryLogs.listRecent`
 * (specs/exploration/stub_orphan_inventory.md §4 Twin #2).
 *
 * The previous body of this screen was a thin re-export delegating
 * to the prototype's mock-backed `_bundle.tsx`. T314 lifts the
 * "Tareas en segundo plano" surface onto the real backgroundTasks
 * query so the screen renders honest data (or an honest empty state
 * when the cron rollup has no rows yet), and the FR-014 (T-C) test
 * sees the real twin reference somewhere in the screen tree.
 *
 * The wider prototype shell — signal-input cards, dependency lanes,
 * incidents — is re-exported from `_bundle.tsx` to preserve the
 * page composition until those panels are wired in a later task.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { EmptyState, Panel, Pill, Skeleton } from '../../components/primitives';
import { SystemHealth as SystemHealthShell } from './_bundle';

interface TaskSummary {
  subTaskName: string;
  lastRunAt: number | null;
  lastStatus: string | null;
  avgDurationMs: number | null;
}

export function SystemHealth() {
  // Real twin (FR-014 #2): the cron-fed rollup, NOT the stub.
  // `api.deliveryLogs.listRecent` (the stub namesake) is deliberately
  // unreferenced anywhere in frontend-v3/src/screens/**.
  const recent = useQuery(api.backgroundTasks.listRecent, {}) as
    | TaskSummary[]
    | undefined;

  let backgroundTasksPanel: React.ReactNode;
  if (recent === undefined) {
    backgroundTasksPanel = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Skeleton h={48} />
        <Skeleton h={48} />
        <Skeleton h={48} />
      </div>
    );
  } else if (recent.length === 0) {
    backgroundTasksPanel = (
      <EmptyState
        icon="refresh"
        title="Sin ejecuciones recientes"
        body="No hay registros de tareas en los últimos 7 días."
      />
    );
  } else {
    backgroundTasksPanel = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {recent.map((t, i) => (
          <div
            key={t.subTaskName}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '13px 20px',
              borderBottom:
                i < recent.length - 1 ? '1px solid var(--lightest-gray)' : 'none',
            }}
          >
            <span style={{ flex: 1, fontSize: 14 }}>{t.subTaskName}</span>
            <span
              className="t-mono"
              style={{ fontSize: 11, color: 'var(--muted-slate)' }}
            >
              {t.lastRunAt
                ? new Date(t.lastRunAt).toLocaleString('es-MX')
                : '—'}
            </span>
            <Pill
              tone={t.lastStatus === 'success' ? 'good' : 'warn'}
              size="sm"
            >
              {t.lastStatus ?? '—'}
            </Pill>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <SystemHealthShell />
      <Panel
        title="Tareas en segundo plano (en vivo)"
        pad={0}
        style={{ marginTop: 24 }}
      >
        {backgroundTasksPanel}
      </Panel>
    </div>
  );
}

export default SystemHealth;
